import { useState, useEffect, useRef } from 'react';
import DatePicker from 'react-datepicker';
import Select from 'react-select';
import { getAllRestaurants } from '../../utils/superAdminApi';
import { fetchOrders } from '../../utils/api';
import { saveAs } from 'file-saver';
import { 
  FaFileCsv, 
  FaCalendarAlt, 
  FaPrint, 
  FaChartLine,
  FaReceipt,
  FaStore,
  FaChevronDown,
  FaChevronUp
} from 'react-icons/fa';
import { toast, Toaster } from 'react-hot-toast';
import { format } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';
import 'react-datepicker/dist/react-datepicker.css';

// Simple Modal for Report Type and Calendar
const Modal = ({ open, onClose, children }) => {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40"
      onClick={onClose}
    >
      <div
        className="bg-gray-800 rounded-lg shadow-lg p-6 min-w-[250px] relative"
        onClick={e => e.stopPropagation()}
      >
        {children}
        <button onClick={onClose} className="absolute top-2 right-2 text-gray-400 hover:text-white text-lg">&times;</button>
      </div>
    </div>
  );
};

const SuperAdminReports = () => {
  // State management
  const [loadingReport, setLoadingReport] = useState(false);
  const [restaurants, setRestaurants] = useState([]);
  const [filters, setFilters] = useState({
    dateRange: [new Date(new Date().setDate(new Date().getDate() - 30)), new Date()],
    selectedRestaurants: [],
    reportType: 'revenue',
    orderStatus: 'completed',
    paymentMethod: 'all',
    exportFormat: 'csv'
  });
  const [isLoadingRestaurants, setIsLoadingRestaurants] = useState(false);
  const [reportPreview, setReportPreview] = useState(null);
  const [showFilters, setShowFilters] = useState(true);
  const [reportTypeModal, setReportTypeModal] = useState(false);
  const [dateModalOpen, setDateModalOpen] = useState(false);
  const tableRef = useRef();

  // Fetch restaurants on mount
  useEffect(() => {
    const fetchRestaurants = async () => {
      setIsLoadingRestaurants(true);
      try {
        const { restaurants } = await getAllRestaurants({ limit: 1000 });
        setRestaurants(restaurants.map(r => ({ 
          value: r.id, 
          label: r.name,
          status: r.status 
        })));
      } catch (error) {
        toast.error('Failed to load restaurants');
      } finally {
        setIsLoadingRestaurants(false);
      }
    };
    fetchRestaurants();
  }, []);

  // Helper: fetch and combine orders for multiple restaurants
  const fetchOrdersForRestaurants = async (restaurantIds, startDate, endDate, orderStatus, reportType) => {
    let allOrders = [];
    for (const r of restaurantIds) {
      const orders = await fetchOrders(r.value, startDate, endDate);
      // Add restaurant name to each order
      const restaurantName = r.label;
      orders.forEach(order => order.restaurantName = restaurantName);
      allOrders = allOrders.concat(orders);
    }
    if (reportType === 'orders' && orderStatus && orderStatus !== 'all') {
      allOrders = allOrders.filter(order => (order.status || '').toLowerCase() === orderStatus.toLowerCase());
    }
    if (reportType === 'revenue') {
      allOrders = allOrders.filter(order => (order.status || '').toLowerCase() === 'completed');
    }
    return allOrders;
  };

  // Helper: fetch and filter restaurants by registration date
  const fetchRestaurantsByDate = async (startDate, endDate) => {
    const { restaurants } = await getAllRestaurants({ limit: 1000 });
    return restaurants.filter(r => {
      if (!r.createdAt) return false;
      const created = new Date(r.createdAt._seconds ? r.createdAt._seconds * 1000 : r.createdAt);
      return created >= startDate && created <= endDate;
    });
  };

  // Main report fetching handler
  const handleGenerateReport = async () => {
    setLoadingReport(true);
    setReportPreview(null);
    try {
      const [startDate, endDate] = filters.dateRange;
      if (filters.reportType === 'orders' || filters.reportType === 'revenue') {
        if (!filters.selectedRestaurants.length) {
          toast.error('Please select at least one restaurant');
          setLoadingReport(false);
          return;
        }
        const orders = await fetchOrdersForRestaurants(
          filters.selectedRestaurants,
        startDate,
        endDate,
          filters.orderStatus,
          filters.reportType
        );
        setReportPreview(orders);
      } else if (filters.reportType === 'restaurants') {
        const restaurants = await fetchRestaurantsByDate(startDate, endDate);
        setReportPreview(restaurants);
      }
    } catch (error) {
      toast.error('Failed to fetch report data');
      setReportPreview([]);
    } finally {
      setLoadingReport(false);
    }
  };

  // Report type options
  const reportTypes = [
    {
      value: 'revenue',
      label: 'Revenue',
      icon: <FaChartLine className="mr-2" />,
      description: 'Financial breakdown including commissions'
    },
    {
      value: 'orders',
      label: 'Orders',
      icon: <FaReceipt className="mr-2" />,
      description: 'Order details with status and timestamps'
    },
    {
      value: 'restaurants',
      label: 'Restaurants',
      icon: <FaStore className="mr-2" />,
      description: 'Registered restaurants with performance'
    }
  ];

  // CSV export utility
  const exportToCSV = (data, columns, filename = 'report.csv') => {
    const header = columns.map(col => col.label).join(',');
    const rows = data.map(row =>
      columns.map(col => {
        let val = row[col.key];
        if (val === undefined || val === null) return '';
        if (typeof val === 'string' && val.includes(',')) return '"' + val + '"';
        return val;
      }).join(',')
    );
    const csvContent = [header, ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, filename);
  };

  // Print handler
  const handlePrint = () => {
    const printContents = tableRef.current.innerHTML;
    const win = window.open('', '', 'height=700,width=900');
    win.document.write('<html><head><title>Report</title>');
    win.document.write('<style>body{font-family:sans-serif;}table{width:100%;border-collapse:collapse;}th,td{border:1px solid #ccc;padding:8px;text-align:left;}th{background:#eee;}</style>');
    win.document.write('</head><body>');
    win.document.write(printContents);
    win.document.write('</body></html>');
    win.document.close();
    win.print();
  };

  return (
    <div className="p-6 bg-gray-900 min-h-screen">
      <Toaster position="top-right" reverseOrder={false} />
      
      {/* Page Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Reports Dashboard</h1>
          <p className="text-gray-400 text-sm mt-1">
            Generate detailed reports for platform analytics and accounting
          </p>
        </div>
        <div className="mt-4 md:mt-0 flex gap-3">
          <button 
            className="bg-gray-800 hover:bg-gray-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-all"
            onClick={() => setShowFilters(!showFilters)}
          >
            {showFilters ? <FaChevronUp /> : <FaChevronDown />}
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </button>
          <button className="bg-gray-800 hover:bg-gray-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-all" onClick={handlePrint}>
            <FaPrint /> Print
          </button>
          <button className="bg-gray-800 hover:bg-gray-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-all" onClick={() => {
            if (!reportPreview || !reportPreview.length) return;
            let columns;
            if (filters.reportType === 'orders' || filters.reportType === 'revenue') {
              columns = [
                { label: 'Order ID', key: 'id' },
                { label: 'Restaurant', key: 'restaurantName' },
                { label: 'Date', key: 'createdAt' },
                { label: 'Total', key: 'totalGross' },
              ];
              if (filters.reportType === 'orders') columns.push({ label: 'Status', key: 'status' });
            } else {
              columns = [
                { label: 'ID', key: 'id' },
                { label: 'Name', key: 'name' },
                { label: 'Owner', key: 'owner.displayName' },
                { label: 'Status', key: 'status' },
                { label: 'Joined At', key: 'createdAt' },
              ];
            }
            exportToCSV(reportPreview.map(row => ({ ...row, totalGross: (row.total * 1.05).toFixed(2) })), columns, `${filters.reportType}-report.csv`);
          }}>
            <FaFileCsv /> CSV
          </button>
        </div>
      </div>

      {/* Filters Panel - Horizontal */}
      <AnimatePresence>
        {showFilters && (
          <motion.div 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="bg-gray-800 p-4 rounded-xl border border-gray-700 mb-6 overflow-hidden"
          >
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Report Type Modal Button */}
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1 uppercase tracking-wider">
              Report Type
            </label>
                <button
                  className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm bg-gray-700 text-white hover:bg-gray-600 transition-all border border-gray-600"
                  onClick={() => setReportTypeModal(true)}
                  type="button"
                >
                  <span className="flex items-center gap-2">
                    {reportTypes.find(t => t.value === filters.reportType)?.icon}
                    {reportTypes.find(t => t.value === filters.reportType)?.label}
                  </span>
                  <FaChevronDown className="ml-2" />
                </button>
                <Modal open={reportTypeModal} onClose={() => setReportTypeModal(false)}>
                  <div className="mb-4 text-lg font-semibold text-white">Select Report Type</div>
            <div className="space-y-2">
              {reportTypes.map(type => (
                      <button
                  key={type.value}
                        onClick={() => {
                          setFilters({ ...filters, reportType: type.value });
                          setReportTypeModal(false);
                        }}
                        className={`w-full flex items-center gap-2 px-4 py-2 text-sm rounded-lg transition-all text-left ${filters.reportType === type.value ? 'bg-purple-600 text-white' : 'text-gray-200 hover:bg-gray-600'}`}
                >
                    {type.icon}
                    {type.label}
                      </button>
                    ))}
                  </div>
                </Modal>
          </div>

          {/* Restaurant Selection */}
          {filters.reportType !== 'restaurants' && (
                <div>
                  <label className="block text-xs font-medium text-gray-400 mb-1 uppercase tracking-wider">
                Restaurants
              </label>
              <Select
                isMulti
                options={restaurants}
                isLoading={isLoadingRestaurants}
                value={filters.selectedRestaurants}
                onChange={selected => setFilters({ ...filters, selectedRestaurants: selected })}
                    placeholder="Search restaurants..."
                    className="react-select-container text-sm"
                classNamePrefix="react-select"
                isClearable
                closeMenuOnSelect={false}
                    menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                    menuPosition="fixed"
                    filterOption={(option, inputValue) =>
                      option.label.toLowerCase().includes(inputValue.toLowerCase())
                    }
                    styles={{
                      control: (base) => ({
                        ...base,
                        backgroundColor: '#374151',
                        borderColor: '#4B5563',
                        minHeight: '40px'
                      }),
                      menu: (base) => ({
                        ...base,
                        backgroundColor: '#374151',
                        borderColor: '#4B5563'
                      }),
                      option: (base, { isFocused, isSelected }) => ({
                        ...base,
                        backgroundColor: isSelected 
                          ? '#9333ea' 
                          : isFocused 
                            ? '#4B5563' 
                            : '#374151',
                        color: isSelected ? 'white' : '#D1D5DB'
                      }),
                      multiValue: (base) => ({
                        ...base,
                        backgroundColor: '#4B5563'
                      }),
                      multiValueLabel: (base) => ({
                        ...base,
                        color: 'white'
                      }),
                      placeholder: (base) => ({
                        ...base,
                        color: '#9CA3AF'
                      }),
                      singleValue: (base) => ({
                        ...base,
                        color: 'white'
                      }),
                      menuPortal: base => ({ ...base, zIndex: 9999 })
                    }}
                  />
            </div>
          )}

          {/* Date Range */}
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1 uppercase tracking-wider">
              Date Range
            </label>
            <div className="relative">
              <button
                type="button"
                onClick={() => setDateModalOpen(true)}
                className="w-full flex items-center bg-gray-700 border border-gray-600 rounded-lg py-2 pl-3 pr-4 text-white focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm transition-all"
              >
                <FaCalendarAlt className="mr-2 text-purple-400" />
                <span className="truncate">
                  {filters.dateRange[0] && filters.dateRange[1]
                    ? `${format(filters.dateRange[0], 'MMM d, yyyy')} - ${format(filters.dateRange[1], 'MMM d, yyyy')}`
                    : 'Select date range'}
                </span>
              </button>
              <Modal open={dateModalOpen} onClose={() => { setDateModalOpen(false); }}>
                <div className="mb-2 text-lg font-semibold text-white">Select Date Range</div>
                <div className="mb-4 text-sm text-purple-300">
                  {(!filters.dateRange[0] || (filters.dateRange[0] && filters.dateRange[1])) ? 'Select start date' : 'Select end date'}
                </div>
                <DatePicker
                  inline
                  selectsRange
                  startDate={filters.dateRange[0]}
                  endDate={filters.dateRange[1]}
                  onChange={update => {
                    setFilters({ ...filters, dateRange: update });
                    // Close modal if both dates are selected
                    if (update[0] && update[1]) setTimeout(() => setDateModalOpen(false), 300);
                  }}
                  monthsShown={2}
                  calendarClassName="bg-gray-800 text-white rounded-lg shadow-lg border border-gray-700"
                />
              </Modal>
            </div>
          </div>

              {/* Generate Button */}
              <div className="flex items-end">
          <button
            onClick={handleGenerateReport}
            disabled={loadingReport}
                  className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-medium py-2 px-4 rounded-lg disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all"
          >
            {loadingReport ? (
              <>
                      <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Generating...
              </>
            ) : (
              <>
                <FaFileCsv />
                Generate Report
              </>
            )}
          </button>
              </div>
            </div>

            {/* Additional Filters - Collapsible */}
            <div className="mt-4 pt-4 border-t border-gray-700">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Export Options */}
                

                {/* Order Status Filter */}
                {filters.reportType === 'orders' && (
                <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1 uppercase tracking-wider">
                      Order Status
                    </label>
                    <select
                      value={filters.orderStatus}
                      onChange={e => setFilters({ ...filters, orderStatus: e.target.value })}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2 px-3 text-white focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                    >
                      <option value="all">All Statuses</option>
                      <option value="pending">Pending</option>
                      
                  
                      <option value="completed">Completed</option>
                      
                    </select>
                </div>
                )}

                
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <div className="space-y-6">
          {/* Report Preview */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-gradient-to-br from-gray-800 to-gray-700 p-6 rounded-xl border border-gray-700 shadow-lg"
        >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-white">
              {reportTypes.find(t => t.value === filters.reportType)?.label} Report
              </h2>
              <div className="text-sm text-gray-400">
                {filters.dateRange[0] && filters.dateRange[1] ? (
                  `${format(filters.dateRange[0], 'MMM d, yyyy')} - ${format(filters.dateRange[1], 'MMM d, yyyy')}`
              ) : 'All time'}
              </div>
            </div>

            {/* Report Preview Table */}
            <div ref={tableRef}>
              {loadingReport ? (
                <div className="text-center text-gray-400 py-12">Loading report data...</div>
              ) : reportPreview && reportPreview.length > 0 ? (
                <div className="overflow-x-auto">
                  <div className="max-h-96 overflow-y-auto">
                    <table className="min-w-full divide-y divide-gray-700">
                      <thead className="bg-gray-700 sticky top-0 z-10">
                        <tr>
                          {filters.reportType === 'orders' || filters.reportType === 'revenue' ? (
                            <>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">#</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Order ID</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Restaurant</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Date</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Total</th>
                              {filters.reportType === 'orders' && (
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Status</th>
                              )}
                            </>
                          ) : (
                            <>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">#</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">ID</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Name</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Owner</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Status</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Joined At</th>
                            </>
                          )}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-700">
                        {filters.reportType === 'orders' || filters.reportType === 'revenue' ? (
                          <>
                            {reportPreview.map((row, idx) => (
                              <tr key={idx} className="hover:bg-gray-700/50 transition-colors">
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300">{idx + 1}</td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300">{row.id}</td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300">{row.restaurantName}</td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300">{row.createdAt ? format(new Date(row.createdAt._seconds ? row.createdAt._seconds * 1000 : row.createdAt), 'MMM d, yyyy h:mm a') : (row.date ? format(new Date(row.date), 'MMM d, yyyy h:mm a') : '')}</td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300">₹{(row.total * 1.05).toFixed(2)}</td>
                                {filters.reportType === 'orders' && (
                                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300">{row.status}</td>
                                )}
                              </tr>
                            ))}
                            {/* Total row */}
                            <tr className="bg-gray-900 font-bold">
                              <td className="px-4 py-3 text-right text-sm text-gray-200" colSpan={filters.reportType === 'orders' ? 4 : 4}>Total</td>
                              <td className="px-4 py-3 text-sm text-purple-300">₹{reportPreview.reduce((sum, row) => sum + ((Number(row.total) || 0) * 1.05), 0).toFixed(2)}</td>
                              {filters.reportType === 'orders' && <td></td>}
                            </tr>
                          </>
                        ) : (
                          reportPreview.map((row, idx) => (
                            <tr key={idx} className="hover:bg-gray-700/50 transition-colors">
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300">{idx + 1}</td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300">{row.id}</td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300">{row.name}</td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300">{row.owner?.displayName || '-'}</td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300">{row.status}</td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300">{row.createdAt ? format(new Date(row.createdAt._seconds ? row.createdAt._seconds * 1000 : row.createdAt), 'MMM d, yyyy') : ''}</td>
                          </tr>
                            ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="bg-gray-900/50 rounded-lg p-8 text-center border-2 border-dashed border-gray-700">
                <FaFileCsv className="mx-auto text-4xl text-gray-600 mb-4" />
                <h3 className="text-lg font-medium text-gray-300 mb-2">
                  Report Preview
                </h3>
                <p className="text-gray-500 max-w-md mx-auto">
                  {filters.reportType === 'revenue' 
                    ? 'Revenue report will show detailed financial breakdown including commissions, taxes, and net amounts.'
                    : filters.reportType === 'orders'
                    ? 'Orders report will display order IDs, timestamps, statuses, items, and totals.'
                    : 'Restaurants report will list all registered restaurants with their status and performance metrics.'}
                </p>
                  <button 
                    onClick={handleGenerateReport}
                    className="mt-4 bg-purple-600 hover:bg-purple-700 text-white font-medium py-2 px-4 rounded-lg inline-flex items-center gap-2 transition-all"
                  >
                    <FaFileCsv /> Generate Report Preview
                </button>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default SuperAdminReports;